import Link from 'next/link';
import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCard,
  DocsCardGrid,
  DocsStep,
  DocsSteps,
  DocsTable,
} from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'roles', title: '1. The roles' },
  { id: 'admin-permissions', title: '2. Admin permissions' },
  { id: 'staff-permissions', title: '3. Staff permissions' },
  { id: 'seats', title: '4. Seats' },
  { id: 'inviting', title: '5. Inviting someone' },
  { id: 'practice', title: '6. Getting the model right' },
];

const TeamRolesPage = () => {
  return (
    <>
      <NextSeo
        title="Team, Roles & Seats"
        description="How Chat.co splits access between owners, admins and team members, what each permission actually controls, and how team seats are counted and expanded."
        canonical="https://chat.co/docs/team-roles"
        openGraph={{
          url: 'https://chat.co/docs/team-roles',
          title: 'Team, Roles & Seats | Chat.co',
          description:
            'Owners, admins and team members: what each permission controls, and how seats are counted.',
          type: 'article',
          article: {
            publishedTime: '2026-08-30T00:00:00Z',
            modifiedTime: '2026-08-30T00:00:00Z',
            section: 'Operations',
            tags: ['team', 'permissions', 'roles', 'seats', 'access control'],
          },
        }}
        twitter={{ cardType: 'summary_large_image' }}
      />

      <DocsArticleLayout
        category="Operations"
        categoryColor="text-blue-700"
        title="Team, roles &amp; seats"
        description="Who can do what, where that is configured, and how seats are counted — so nobody shares a login."
        readingTime="8 min read"
        lastUpdated="August 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Managing multiple bots & teams',
          href: PageLink.docsMultiBotOrchestration,
        }}
        nextArticle={{
          title: 'Enterprise SSO',
          href: PageLink.docsEnterpriseSso,
        }}
        keyTakeaways={[
          'Admins and team members are managed together on one Team page',
          'Admin permissions are account-wide toggles; team member permissions are per bot',
          'Team member permissions are granular: view, add, edit and delete, per feature',
          'Admins and team members draw from a single shared seat pool',
          'Deleting a chatbot can be delegated to an admin as a request the owner approves',
        ]}>
        <p className="lead">
          Two people sharing a login is the most common access-control failure on any SaaS product,
          and it is almost always a symptom of a permission model nobody understood. Chat.co&apos;s
          is genuinely simple once you see the split: <strong>admins</strong> get account-wide
          toggles, <strong>team members</strong> get per-bot, per-action grants.
        </p>

        <section id="roles" className="scroll-mt-28">
          <h2>1. The roles</h2>
          <p>
            Everything is managed from <strong>Team</strong> in the main sidebar — one page covering
            both admins and staff.
          </p>

          <DocsTable
            headers={['Role', 'Scope', 'Typical person']}
            rows={[
              [
                <strong key="o">Business Owner</strong>,
                'Everything, including billing and account deletion. There is one.',
                'The person whose card is on file',
              ],
              [
                <strong key="a">Admin</strong>,
                'Account-wide, limited by a set of permission toggles the owner sets',
                'An operations lead or second-in-command',
              ],
              [
                <strong key="t">Team member</strong>,
                'Per bot, per feature, per action — nothing by default',
                'Support agents, content editors, contractors',
              ],
            ]}
          />

          <DocsCallout variant="note" title="Team members start with nothing">
            A newly invited team member has no permissions at all until you grant them. That is the
            right default — it means an over-permissioned account is always a decision someone made,
            never an accident of onboarding.
          </DocsCallout>
        </section>

        <section id="admin-permissions" className="scroll-mt-28">
          <h2>2. Admin permissions</h2>
          <p>Each admin carries an independent set of account-wide toggles:</p>

          <DocsTable
            headers={['Permission', 'Grants']}
            rows={[
              [<strong key="1">Chatbot creation</strong>, 'Create new bots'],
              [<strong key="2">Chat logs</strong>, 'Read conversations across bots'],
              [<strong key="3">Knowledge base</strong>, 'Manage documents, links, text and Q&A'],
              [<strong key="4">Configuration</strong>, 'Change bot settings and behaviour'],
              [
                <strong key="5">Team management</strong>,
                'Invite and manage other admins and team members',
              ],
              [<strong key="6">Billing</strong>, 'See and change the subscription'],
              [
                <strong key="7">Delete chatbots</strong>,
                'Raise a deletion request — the owner still has to approve it',
              ],
              [
                <strong key="8">Voice line</strong>,
                'Edit phone line settings, voices, numbers and recordings. Without it, view-only.',
              ],
            ]}
          />

          <DocsCallout variant="warning" title="Two toggles deserve a second thought">
            <strong>Team management</strong> lets an admin grant permissions to others, including
            other admins — it is effectively the power to expand their own reach.{' '}
            <strong>Billing</strong> exposes and can change what you pay. Grant both deliberately,
            not as part of a default admin bundle.
          </DocsCallout>

          <p>
            Deletion is handled as a two-step on purpose. An admin with the delete permission can
            request removal of a chatbot; the owner approves it. Destroying a trained knowledge base
            is not something anyone should be able to do alone in a single click.
          </p>
        </section>

        <section id="staff-permissions" className="scroll-mt-28">
          <h2>3. Staff permissions</h2>
          <p>
            Team member access is granted <strong>per bot</strong>, and within each bot per feature
            and per action.
          </p>

          <h3>Features</h3>
          <DocsTable
            headers={['Feature', 'Covers']}
            rows={[
              [<strong key="1">Chat logs</strong>, 'Conversation history, and the Handoff queue'],
              [
                <strong key="2">Knowledge base</strong>,
                'Documents, links, text and the Knowledge Gaps screen',
              ],
              [<strong key="3">Manage leads</strong>, 'The Leads screen and lead exports'],
              [
                <strong key="4">Q&amp;A</strong>,
                'Q&A pairs (kept in step with knowledge base in the UI)',
              ],
              [
                <strong key="5">Tune bot</strong>,
                'Model, system prompt, creativity, and bot settings',
              ],
              [
                <strong key="6">Appearance</strong>,
                'Widget look and feel, and deployment settings',
              ],
            ]}
          />

          <h3>Actions</h3>
          <p>
            Each feature carries four independent flags — <strong>view</strong>,{' '}
            <strong>add</strong>, <strong>edit</strong> and <strong>delete</strong>. That
            granularity is what makes the useful configurations possible:
          </p>

          <DocsCardGrid columns={3}>
            <DocsCard icon="tabler:headset" title="Support agent">
              Chat logs: view. Leads: view. Nothing else. Can read and act on conversations, cannot
              change how the bot behaves.
            </DocsCard>
            <DocsCard icon="tabler:writing" title="Content editor">
              Knowledge base and Q&amp;A: view, add, edit. No delete, no tune bot. Can improve
              answers, cannot remove your corpus or change the model.
            </DocsCard>
            <DocsCard icon="tabler:eye" title="Read-only stakeholder">
              View on everything, add/edit/delete on nothing. Perfect for the person who wants
              visibility without the ability to break something.
            </DocsCard>
          </DocsCardGrid>

          <DocsCallout variant="tip" title="Withhold delete by default">
            View, add and edit cover almost every legitimate day-to-day need. Delete is the only one
            of the four that destroys work, and it is rarely urgent. Grant it to the people who
            genuinely curate the corpus, and nobody else.
          </DocsCallout>
        </section>

        <section id="seats" className="scroll-mt-28">
          <h2>4. Seats</h2>
          <p>
            Admins and team members draw from a <strong>single shared seat pool</strong>. There is
            no separate admin allowance — adding an admin consumes a seat exactly as adding a team
            member does.
          </p>

          <DocsTable
            headers={['Plan', 'Team seats']}
            rows={[
              ['Starter', '1'],
              ['Pro', '1'],
              ['Business', '10'],
              ['Enterprise', 'Unlimited'],
            ]}
          />

          <p>
            Need more without changing plan? Seats are available as an add-on — the Team page offers
            the purchase flow directly when you hit the limit, so you are not sent hunting through
            billing.
          </p>

          <DocsCallout variant="warning" title="A shared login is not a saved seat">
            It is the cheapest-looking and most expensive option available: you lose the audit
            trail, you cannot revoke one person&apos;s access, and offboarding means rotating a
            password everyone knows. A seat costs less than the first incident.
          </DocsCallout>
        </section>

        <section id="inviting" className="scroll-mt-28">
          <h2>5. Inviting someone</h2>

          <DocsSteps>
            <DocsStep title="Open Team">
              <p>
                From the main sidebar. Admins and team members are both managed here — the sections
                are separate but the page is one.
              </p>
            </DocsStep>
            <DocsStep title="Decide which role they need">
              <p>
                Account-wide responsibility is an admin. Work on specific bots is a team member.
                When it is genuinely ambiguous, team member is the safer starting point — it is
                easier to add access later than to explain why someone had it.
              </p>
            </DocsStep>
            <DocsStep title="Invite by email">
              <p>They receive an invitation and set up their own credentials.</p>
            </DocsStep>
            <DocsStep title="Set permissions before they accept, not after">
              <p>
                For a team member, choose the bots and the per-feature actions. Doing this up front
                avoids the window where someone is logged in and nobody has decided what they should
                see.
              </p>
            </DocsStep>
          </DocsSteps>
        </section>

        <section id="practice" className="scroll-mt-28">
          <h2>6. Getting the model right</h2>

          <h3>Grant the narrowest thing that works</h3>
          <p>
            Start from nothing and add what the person is actually blocked on. The opposite approach
            — grant broadly and trim later — never gets trimmed.
          </p>

          <h3>Review when people change roles</h3>
          <p>
            The permissions someone needed in their last role follow them into the next one unless
            somebody looks. A quarterly pass over the Team page takes ten minutes.
          </p>

          <h3>Separate audiences with bots, not permissions</h3>
          <p>
            Permissions control who can administer a bot. They do not filter what a bot knows. If a
            document must not reach a particular audience, it belongs in a different bot — see{' '}
            <Link href={PageLink.docsMultiBotOrchestration}>managing multiple bots</Link>.
          </p>

          <h3>Use SSO once you are past a handful of people</h3>
          <p>
            With <Link href={PageLink.docsEnterpriseSso}>OIDC single sign-on</Link>, access follows
            your identity provider — so offboarding someone there offboards them here, with no
            manual step to forget.
          </p>

          <DocsCardGrid columns={2}>
            <DocsCard icon="tabler:lock" title="Enterprise SSO" href={PageLink.docsEnterpriseSso}>
              Centralise sign-in on your identity provider.
            </DocsCard>
            <DocsCard
              icon="tabler:shield-check"
              title="Security &amp; compliance"
              href={PageLink.docsSecurityCompliance}>
              The wider access-control and data-handling picture.
            </DocsCard>
          </DocsCardGrid>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default TeamRolesPage;
